import { useEffect, useState } from "react";
import Loading from "../../components/Loading";
import { getAramexSheet, saveAramexSheet } from "../../actions/studensts";

// The Aramex shipping sheet: one row per student sitting in the Aramex state.
// Every column is editable here and saved back to aramex_data. Name is
// pre-filled from arabic_data and the phones from the students phone field, so
// editing either only overrides the sheet's copy — never the source record.
export default function AramexSheetModal({ onClose }) {
  const username = JSON.parse(localStorage.getItem("profile"))?.value[0]
    ?.username;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const confirmedCount = rows.filter((row) => row.confirmed).length;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getAramexSheet();
      if (cancelled) return;
      if (res?.status === 200) {
        setRows(res.data.data);
      } else {
        setError("Failed to load the Aramex sheet. Please try again.");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (studentId, field, value) => {
    setSavedMessage("");
    setRows((prev) =>
      prev.map((row) =>
        row.student_id === studentId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSavedMessage("");
    const payload = rows.map((row) => ({
      student_id: row.student_id,
      name: row.name,
      address: row.address,
      city: row.city,
      phone_number: row.phone_number,
      phone_number2: row.phone_number2,
      confirmed: row.confirmed,
    }));
    const res = await saveAramexSheet(payload, username);
    setSaving(false);
    if (res?.status === 200) {
      setSavedMessage(`✓ Saved ${res.data.saved} rows.`);
    } else {
      setError("Saving failed. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-blue-900 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Aramex Shipping Sheet</h2>
            <p className="text-blue-200 text-sm mt-0.5">
              {loading
                ? "Loading..."
                : `${rows.length} students in Aramex · ${confirmedCount} confirmed`}{" "}
              · check the row, then tick Confirm
            </p>
          </div>
          <button
            onClick={onClose}
            className="cursor-pointer rounded-full w-9 h-9 bg-red-400 text-white
            flex items-center justify-center active:scale-90 hover:bg-red-500"
          >
            X
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loading />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-gray-500 text-center py-10">
              No students in the Aramex state.
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0">
                <tr className="bg-blue-100 text-blue-900">
                  <th className="border px-3 py-2 text-left">NAME</th>
                  <th className="border px-3 py-2 text-left">ADDRESS</th>
                  <th className="border px-3 py-2 text-left">CITY</th>
                  <th className="border px-3 py-2 text-left">PHONE NUMBER</th>
                  <th className="border px-3 py-2 text-left">PHONE NUMBER2</th>
                  <th className="border px-3 py-2 text-center">CONFIRMED</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.student_id}
                    className={row.confirmed ? "bg-green-50" : "odd:bg-gray-50"}
                  >
                    <td className="border px-1 py-1">
                      <input
                        type="text"
                        dir="rtl"
                        className="w-full px-2 py-1 border rounded outline-blue-600"
                        value={row.name}
                        placeholder={row.full_name || "Name"}
                        onChange={(e) =>
                          handleChange(row.student_id, "name", e.target.value)
                        }
                      />
                    </td>
                    <td className="border px-1 py-1">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border rounded outline-blue-600"
                        value={row.address}
                        placeholder="Address"
                        onChange={(e) =>
                          handleChange(row.student_id, "address", e.target.value)
                        }
                      />
                    </td>
                    <td className="border px-1 py-1">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border rounded outline-blue-600"
                        value={row.city}
                        placeholder="City"
                        onChange={(e) =>
                          handleChange(row.student_id, "city", e.target.value)
                        }
                      />
                    </td>
                    <td className="border px-1 py-1">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border rounded outline-blue-600"
                        value={row.phone_number}
                        placeholder="Phone number"
                        onChange={(e) =>
                          handleChange(
                            row.student_id,
                            "phone_number",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="border px-1 py-1">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border rounded outline-blue-600"
                        value={row.phone_number2}
                        placeholder="Phone number 2"
                        onChange={(e) =>
                          handleChange(
                            row.student_id,
                            "phone_number2",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="border px-3 py-2 text-center">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-green-600 cursor-pointer"
                          checked={!!row.confirmed}
                          onChange={(e) =>
                            handleChange(
                              row.student_id,
                              "confirmed",
                              e.target.checked
                            )
                          }
                        />
                        <span
                          className={`text-xs font-medium ${
                            row.confirmed ? "text-green-700" : "text-gray-400"
                          }`}
                        >
                          {row.confirmed ? "Confirmed" : "Not confirmed"}
                        </span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t px-6 py-4 flex items-center justify-between gap-4">
          <span className="text-sm">
            {error && <span className="text-red-600">{error}</span>}
            {savedMessage && (
              <span className="text-green-700">{savedMessage}</span>
            )}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-100"
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || rows.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold
              py-2 px-4 rounded disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
